import { useState, useCallback, useRef } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { Input } from '@/components/evo/ui/input';
import useGetProducts from '@/pages/ContractDetails/ContractLines/hooks/useGetProducts';
import { ProductOutputType } from '@/graphql/contracts/_generated_/contracts';

interface ProductSelectorProps {
  value: string;
  onChange: (value: string, stockName?: string) => void;
  className?: string;
}

const PRODUCT_RESULTS_TO_SHOW = 10;

export function ProductSelector({ value, onChange, className }: ProductSelectorProps) {
  const { getProducts, isLoadingGetProducts } = useGetProducts();
  const [searchTerm, setSearchTerm] = useState('');
  const [mergedProducts, setMergedProducts] = useState<ProductOutputType[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductOutputType | null>(null);
  const initialValueRef = useRef<string>(value);
  const initialProductRef = useRef<ProductOutputType | null>(null);

  const handleSearch = useCallback(async (term: string) => {
    setSearchTerm(term);
    if (term.length >= 2) {
      setIsSearching(true);
      const [byCode, byName] = await Promise.all([
        getProducts({
          filter: { stockCode: { contains: term } },
          first: PRODUCT_RESULTS_TO_SHOW,
        }),
        getProducts({
          filter: { stockName: { contains: term } },
          first: PRODUCT_RESULTS_TO_SHOW,
        }),
      ]);
      const combined = [...(byCode ?? []), ...(byName ?? [])];
      const seen = new Set<string>();
      const unique: ProductOutputType[] = [];
      for (const p of combined) {
        if (p && p.stockCode && !seen.has(p.stockCode)) {
          seen.add(p.stockCode);
          unique.push(p as ProductOutputType);
        }
      }
      setMergedProducts(unique.slice(0, PRODUCT_RESULTS_TO_SHOW));

      const found = unique.find((p) => p?.stockCode === value);
      if (found) {
        setSelectedProduct(found);
        if (value === initialValueRef.current && !initialProductRef.current) {
          initialProductRef.current = found;
        }
      }

      setIsSearching(false);
    } else {
      setMergedProducts([]);
    }
  }, [getProducts, value]);

  const matchedProduct = selectedProduct?.stockCode === value ? selectedProduct : null;
  
  const displayValue = value
    ? matchedProduct
      ? `[${matchedProduct.stockCode}] - ${matchedProduct.stockName}`
      : value === initialValueRef.current && initialProductRef.current
        ? `[${initialProductRef.current.stockCode}] - ${initialProductRef.current.stockName}`
        : value
    : '';

  const handleValueChange = (newValue: string) => {
    const product = mergedProducts.find((p) => p?.stockCode === newValue);
    if (product) {
      setSelectedProduct(product);
    }
    onChange(newValue, product?.stockName ?? undefined);
  };

  const loading = isLoadingGetProducts || isSearching;

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={handleValueChange}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={loading ? 'Loading...' : 'Select product'}>
            {displayValue}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <div className="p-2">
            <Input
              placeholder="Search by stock code or name (min 2 chars)..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-8"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {loading ? (
            <div className="p-2 text-sm text-gray-500 text-center">Loading...</div>
          ) : mergedProducts.length === 0 ? (
            <div className="p-2 text-sm text-gray-500 text-center">
              {searchTerm.length < 2 ? 'Type at least 2 characters to search' : 'No products found'}
            </div>
          ) : (
            mergedProducts.map((product) => (
              product && (
                <SelectItem key={product.stockCode} value={product.stockCode || ''}>
                  [{product.stockCode}] - {product.stockName}
                </SelectItem>
              )
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
